import React, { useCallback, useEffect, useRef, useState } from 'react';

import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { faFile, faSpinner } from '@fortawesome/fontawesome-free-solid';

import { ReactComponent as AddIcon } from '../../assets/svg/add.svg';
import { ReactComponent as CancelIcon } from '../../assets/svg/cancel.svg';

import styles from './BookmarksBar.module.scss';

import Modal from '../Modal';
import { Fa } from '../elements/fa';
import NoteSearchModal from '../general/Notepad/NoteSearchModal';

import { iFullStoreState, iList, iNote } from '../../shared/interfaces';
import { GMapModalAC, GMapModalContent } from '../../stores/reducers/gMapModal/AC';
import { ACL, UserCan } from '../../shared/constants';
import { iReportDetails, ReportAC, Actions as ReportActions } from '../../stores/reducers/report-reducers';

import { Route, matchPath, useHistory, useLocation, useParams } from "react-router-dom";
import { BOOKMARK_ICONS } from '../../shared/constants';
import { clientDb, keyGen } from '../../shared/firebase';
import { isMobile, makeAudit } from '../../shared/helpers';
import { removeReportTrips } from "../../stores/reducers/devicesTripsPoints/AC";
import { Actions as GeneralActions, useIsLocating, LOCATION_STATUS } from '../../stores/reducers/general-reducers';
import { getStartAndEndOfDate } from "../../utils";
import SearchIcon from '../SVG-components/search';
import MapIcon from '../../assets/svg/map_pointer.svg'
import Pickup from '../../assets/svg/transportation/pickup.svg'
import DriverIcon from '../../assets/svg/driver_24px.svg'
import classNames from "classnames";
import { SearchOptions } from '../../stores/reducers/general-reducers-fn';
import { Button, ButtonStyles } from '../Button';
import { toggleGeoFenceContainerShow } from '../../stores/reducers/fencesContainerUI-actions';
import TimeLine from '../menus/Timeline2';
import AddInventoryDeviceModal from '../pages/devices/AddInventoryDeviceModal/AddInventoryDeviceModal';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
// import { toast } from 'react-toastify';
import { localStorage } from '../../shared/storage';
import * as notesdb from '../../shared/db/notes-db';
import { getAllNoteFields, NoteFieldMap } from '../../shared/db/note-extra-fields-db';
import { useItemNotes } from '../../hooks/useItemNotes';

interface iBookmark {
  id: string;
  name: string;
  url: string;
  icon: string;
  isReport: boolean;
  details?: string;
}

type BookmarksSearchValues = {
  devicesSearchValue: string;
  mapSearchValue: string;
};

interface iBookmarksBar {
  bookmarksSearchValue: BookmarksSearchValues;
  setBookmarksSearchValue: React.Dispatch<React.SetStateAction<BookmarksSearchValues>>;
}

const DeviceTimeline = ({ deviceId }: { deviceId: string }) => {
  const [locateStatus] = useIsLocating(deviceId);

  if (isMobile || locateStatus === LOCATION_STATUS.ACTIVE) {
    return null;
  }

  return <TimeLine deviceId={deviceId} />;
};

const schema = yup.object().shape({
  deviceName: yup
    .string()
    .trim()
    .required('This field is required')
    .min(1, 'This field cannot be empty'),
});

export type NewInventoryDeviceFormValues = {
  deviceName: string;
}

const BookmarksBar = ({ bookmarksSearchValue, setBookmarksSearchValue }: iBookmarksBar) => {
    // Listen for sidebar events to open Add Inventory or Notes Search modals
    useEffect(() => {
      const handleAddInventory = () => setIsCreateInventoryDeviceModalVisible(true);
      const handleNotesSearch = (event: Event) => {
        const customEvent = event as CustomEvent<{ fromSidebarNav?: boolean }>;
        setDefaultNotesSearchToAllFromSidebar(!!customEvent?.detail?.fromSidebarNav);
        setIsNotesSearchOpen(true);
      };
      window.addEventListener('openAddInventoryDeviceModal', handleAddInventory);
      window.addEventListener('openNotesSearchModal', handleNotesSearch);
      return () => {
        window.removeEventListener('openAddInventoryDeviceModal', handleAddInventory);
        window.removeEventListener('openNotesSearchModal', handleNotesSearch);
      };
    }, []);
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [currentUrl, setCurrentUrl] = useState<string>(window.location.href);
  const [bookmarkName, setBookmarkName] = useState<string>('');
  const [errors, setErrors] = useState<string[]>([]);
  const [pickedIcon, setIcon] = useState<string>('');
  const [bookmarks, setBookmarks] = useState<iBookmark[]>([]);
  const [deletingBookmarkId, setDeletingBookmarkId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isCustomUrl, setIsCustomUrl] = useState<boolean>(false);
  const [isCreateInventoryDeviceModalVisible, setIsCreateInventoryDeviceModalVisible] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isNotesSearchOpen, setIsNotesSearchOpen] = useState<boolean>(false);
  const [defaultNotesSearchToAllFromSidebar, setDefaultNotesSearchToAllFromSidebar] = useState<boolean>(false);
  const [globalVisibleFields, setGlobalVisibleFields] = useState<string[]>(['date', 'vehicle', 'partService', 'po', 'vendor', 'note']);
  const [noteFieldDefs, setNoteFieldDefs] = useState<NoteFieldMap>({});

  const hostname = window.location.hostname;

  const user = useSelector((state: any) => state.auth.user);
  const details: iReportDetails = useSelector<iFullStoreState>((state) => state.report.details) as iReportDetails;
  const isStreetView = useSelector<iFullStoreState, boolean>(s => !!s.gmap.streetViewLocation);
  const isGeoFenceContainerVisible = useSelector((s: iFullStoreState) => s.fencesContainerUI.isGeoFenceContainerVisible);

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { id, deviceId } = useParams<{ id?: string; deviceId?: string }>();
  const currentDeviceId = id || deviceId || '';
  const currentDeviceNotes = useItemNotes('device', currentDeviceId || '');
  const isDevicePage = /^\/device\/[^/]+$/.test(location.pathname);

  const userCanDo = useSelector<iFullStoreState, Array<string>>(
    (s) => s.auth.user?.acl?.can || []
  );
  const isSuperAdminACL = ACL.check(UserCan.DO_ANYTHING, userCanDo);
  const canViewSafetyTeam = isSuperAdminACL || ACL.check((UserCan as any).VIEW_SAFETY_TEAM, userCanDo);
  const { register, handleSubmit, errors: inventoryFormErrors } = useForm<NewInventoryDeviceFormValues>({
    resolver: yupResolver(schema),
  });

  const matchDevice = matchPath(location.pathname, {
    path: "/device/:deviceId",
    exact: false,
    strict: false
  });
  const shouldShowTimeline = !!matchDevice;

  const matchGeofence = matchPath(location.pathname, {
    path: ['/tags', '/device', '/:itemType/:itemId'],
    exact: false,
    strict: false
  });
  const shouldShowGeofenceButton = !!matchGeofence && !isStreetView;

  const shouldShowActionsBlock = shouldShowTimeline || shouldShowGeofenceButton;

  const toggleInventoryDeviceModalVisibilyHandler = () => {
    setIsCreateInventoryDeviceModalVisible(prev => !prev);
    setIsSubmitting(false);
  };

  const onCreateNewInventoryDeviceSubmit = async (values: NewInventoryDeviceFormValues) => {
    setIsSubmitting(true);

    const updatedValues = {
      ...values,
      deviceName: values.deviceName.trim(),
    };

    try {
      const newDeviceRef = clientDb()
        .child('devices/device-details')
        .push();

      const newDeviceId = newDeviceRef.key;

      const newDeviceData = {
        id: newDeviceId,
        name: updatedValues.deviceName,
        isInventoryOnlyDevice: true,
      };

      await newDeviceRef.set(newDeviceData);

      toggleInventoryDeviceModalVisibilyHandler();
      history.push(`/device/${newDeviceId}`);
    } catch (err) {
      // toast.error('Error creating device. Please try again.');
      setIsSubmitting(false);
    }
  };

  const toggleGeoFenceContainerVisibility = () => dispatch(toggleGeoFenceContainerShow());
  const toggleLoanerVehiclesVisibility = () =>
    dispatch(
      GMapModalAC.showModal({
        contentType: GMapModalContent.LOANER_ASSIGNMENTS,
      })
    );
  const toggleSafetyTeamVisibility = () =>
    dispatch(
      GMapModalAC.showModal({
        contentType: GMapModalContent.SAFETY_TEAM,
      })
    );

  const openNotesSearch = () => {
    setDefaultNotesSearchToAllFromSidebar(false);
    setIsNotesSearchOpen(true);
  };

  // Debounced Redux dispatch to trigger search
  const debouncedDispatch = useRef(
    debounce((value: string) => {
      dispatch(GeneralActions.CHANGE_TERM(value));
    }, 300)
  ).current;

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedDispatch.cancel();
    };
  }, [debouncedDispatch]);

  const fetchBookmarks = async () => {
    try {
      const bookmarksSnapshot = await clientDb()
        .child(`/people/people-bookmarks/${user.uid}`)
        .once('value');
      const bookmarksData = bookmarksSnapshot.val() || {};

      setBookmarks(Object.values(bookmarksData));
    } catch (error) {
      console.error('Error fetching bookmarks:', error);
    }
  };

  useEffect(() => {
    fetchBookmarks();
  }, [user.uid]);

  useEffect(() => {
    const fetchNoteSettings = async () => {
      try {
        const [fields, defs] = await Promise.all([
          notesdb.getGlobalVisibleFields ? notesdb.getGlobalVisibleFields() : Promise.resolve(null),
          getAllNoteFields().catch(() => ({} as NoteFieldMap))
        ]);

        if (fields && Array.isArray(fields)) setGlobalVisibleFields(fields);
        if (defs) setNoteFieldDefs(defs);
      } catch (e) {
        console.error('Error loading note settings', e);
      }
    };

    fetchNoteSettings();
  }, []);

  useEffect(() => {
    if (!isFormOpen) {
      setErrors([]);
      setBookmarkName('');
      setCurrentUrl(window.location.href);
    }
  }, [isFormOpen]);

  const validateForm = (): string[] => {
    const validationErrors = [];

    if (!currentUrl.trim()) {
      validationErrors.push("Bookmark URL can't be empty");
    }
    else {
      try {
        const url = new URL(currentUrl);

        if (url.hostname !== hostname) {
          validationErrors.push("Invalid hostname");
        }
      } catch (error) {
        validationErrors.push("Invalid URL format");
      }
    }

    if (!bookmarkName.trim()) {
      validationErrors.push("Bookmark name can't be empty");
    }

    return validationErrors;
  };

  const addBookmarkToFirebase = async (isReport: boolean, url: string, details: string) => {
    const bookmarkKey = keyGen();
    const newBookmark = {
      id: bookmarkKey,
      name: bookmarkName,
      url: url,
      icon: pickedIcon || 'settings.svg',
      isReport: isReport,
      details: details
    };

    try {
      await clientDb().update(makeAudit(user, {
        [`people/people-bookmarks/${user.uid}/${bookmarkKey}`]: newBookmark
      }));

      fetchBookmarks();
    } catch (error) {
      console.error('Error adding bookmark:', error);
    }
  };

  const removeBookmarkFromFirebase = async (id: string) => {
    setDeletingBookmarkId(id);

    try {
      await clientDb().update(makeAudit(user, {
        [`people/people-bookmarks/${user.uid}/${id}`]: null
      }));

      fetchBookmarks();
    } catch (error) {
      console.error('Error removing bookmark:', error);
    } finally {
      setTimeout(() => {
        setDeletingBookmarkId(null);
      }, 500);
    }
  };

  const handleAddBookmark = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validateForm();

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const isReport = currentUrl.includes('reports/new');
      const bookmarkDetails = isReport ? JSON.stringify(details) : '';
      await addBookmarkToFirebase(isReport, new URL(currentUrl).hash, bookmarkDetails);
      setIsFormOpen(false);
    } catch (error) {
      console.error('Error adding bookmark:', error);
    }
  };

  const handleBookmarkClick = (bookmark: iBookmark) => {
    if (!bookmark.isReport) {
      window.location.hash = bookmark.url;
      return;
    }

    const details: iReportDetails = bookmark.details ? JSON.parse(bookmark.details) : {};
    const newSearchKey = `bookmark_report_${Date.now()}`;

    updateReportDetails(details, newSearchKey);
    openReportModal();
  };

  // const handleTodaysHistory = () => {
  //   dispatch(removeReportTrips());
  //   dispatch({ type: 'REPORT_RESET_ALL_FILTERS' });

  //   const [startDate, endDate] = getStartAndEndOfDate();
  //   const deviceRegex = /^\/device\/([^\/]+)$/;
  //   const match = location.pathname.match(deviceRegex);
  //   const id = match ? match[1] : null;

  //   if (id) {
  //     dispatch(ReportAC.TOGGLE_ITEM_FILTER('device', id));
  //   }
  //   dispatch(ReportAC.UPSERT_DATE_GROUP_IN_DATES({ startDate, endDate }));

  //   history.push('/reports');
  // }

  const updateReportDetails = (details: iReportDetails, newSearchKey: string) => {
    details.searchKey = newSearchKey;
    details.dates = [{
      startDate: moment(details.dates?.[0]?.startDate ?? Date.now()),
      endDate: moment(details.dates?.[0]?.endDate ?? Date.now())
    }];

    dispatch(ReportActions.SET_SEARCH_KEY(newSearchKey));
    dispatch(ReportActions.SET_DETAILS(details));
  };

  const openReportModal = () => {
    dispatch(
      GMapModalAC.showModal({
        contentType: GMapModalContent.REPORT_PAGE_FILTERS,
      })
    );
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    if (isModalOpen) {
      dispatch(GMapModalAC.hideModal());
    }

    setIsModalOpen(false);
    setIsFormOpen(false);
  };

  const getIconSrc = (icon) => {
    try {
      return require(`../../assets/svg/bookmark/${icon}`);
    } catch (e) {
      try {
        return require(`../../assets/svg/${icon}`);
      } catch (e) {
        return null;
      }
    }
  };

  const isBookmarksBarSearchVisible = process.env.REACT_APP_SHOW_BOOKMARKS_BAR_SEARCH === 'true';

  const handleSearchFocus = async (searchOption: SearchOptions) => {
    await dispatch(GeneralActions.SHOW_BOOKMARKS_SEARCH);
    setBookmarksSearchValue({
      devicesSearchValue: '',
      mapSearchValue: ''
    });
    await dispatch(GeneralActions.CHANGE_SEARCH_BY(searchOption));
  };

  const handleSearchChange = (key: keyof BookmarksSearchValues, value: string) => {
    // Update local state immediately for responsive UI
    setBookmarksSearchValue(prev => ({
      ...prev,
      [key]: value
    }));
    // Trigger search after delay
    debouncedDispatch(value);
  };

  return (
    <div className={styles.BookmarksBarWrapper}>

      {/* Search Vehicles (far left) */}
      {isBookmarksBarSearchVisible && (
        <div className={styles.searchPart} style={{marginRight: 12}}>
          <div className={styles.searchContainer}>
            <SearchIcon className={styles.searchIcon} />
            <input
              className={classNames(styles.searchInput)}
              type="text"
              id="search-input"
              value={bookmarksSearchValue.devicesSearchValue}
              onChange={(e) => handleSearchChange('devicesSearchValue', e.target.value)}
              onFocus={() => handleSearchFocus(SearchOptions.DEVICES)}
              placeholder='Search Vehicles'
            />
          </div>
        </div>
      )}

      {/* Bookmarks */}
      <div className={styles.BookmarksBar}>
        {/* Loaner vehicles */}
        <button
          type="button"
          className={styles.loanerVehiclesButton}
          onClick={toggleLoanerVehiclesVisibility}
        >
          <img
            src={Pickup}
            alt="loaner vehicles"
            className={styles.actionIcon}
            style={{ width: '22px', height: '16px' }}
          />
          <span className={styles.actionLabel}>
            Loaner Vehicles
          </span>
        </button>

        {canViewSafetyTeam && (
          <button
            type="button"
            className={styles.loanerVehiclesButton}
            onClick={toggleSafetyTeamVisibility}
          >
            <img
              src={DriverIcon}
              alt="safety team"
              className={styles.actionIcon}
            />
            <span className={styles.actionLabel}>
              Safety Team
            </span>
          </button>
        )}

        <Button
          className={styles.Bookmark}
          onClick={() => setIsFormOpen(true)}
          styleType={ButtonStyles.NONE}
          style={{ display: 'flex', justifyContent: 'center', width: '40px', padding: '0' }}
        >
          <AddIcon style={{
            height: '20px',
            width: '20px',
            borderRadius: '2px',
            fill: '#111111',
          }} />
        </Button>

        {/* <div className={styles.Bookmark} onClick={() => handleTodaysHistory()}>
          <p>Trip History</p>
        </div> */}
        <div className={styles.BookmarkList}>
          {bookmarks.map((bookmark) => (
          <Button
            styleType={ButtonStyles.NONE}
            key={bookmark.id}
            className={styles.Bookmark}
            onClick={() => handleBookmarkClick(bookmark)}
          >
            {deletingBookmarkId === bookmark.id ? (
              <div className={styles.BookmarkLoader}>
                <Fa icon={faSpinner} spin />
              </div>
            ) : (
              <>
                <img
                  src={getIconSrc(bookmark.icon)}
                  alt={bookmark.icon}
                  style={{ width: '20px', height: '20px' }}
                />
                <p>{bookmark.name}</p>
                <div
                  style={{
                    width: '16px',
                    height: '16px',
                    minWidth: '16px',
                    minHeight: '16px'
                  }}
                >
                  <CancelIcon
                    className={styles.BookmarkDelete}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeBookmarkFromFirebase(bookmark.id);
                    }}
                  />
                </div>
              </>
            )}
          </Button>
        ))}
        </div>
      </div>

      {/* Actions */}
      {shouldShowActionsBlock && (
        <div className={styles.actions}>
          {/* Time line */}
          <Route path="/device/:deviceId">
            <DeviceTimeline deviceId={currentDeviceId} />
          </Route>

          {/* Geofence */}
          {!isStreetView && (
            <Route path={['/tags', '/device', '/:itemType/:itemId']}>
              <button
                type='button'
                className={styles.geoFenceButton}
                onClick={toggleGeoFenceContainerVisibility}
              >
                <img
                  src={MapIcon}
                  alt="layers"
                  className={styles.actionIcon}
                />
                <span className={styles.actionLabel}>
                  {isGeoFenceContainerVisible ? 'Hide' : 'Show'} Geofence
                </span>
              </button>
            </Route>
          )}
        </div>
      )}

      {/* Search Map (right, untouched) */}
      {isBookmarksBarSearchVisible && (
        <div className={styles.searchPart} style={{marginLeft: 'auto'}}>
          <div className={styles.searchContainer}>
            <SearchIcon className={styles.searchIcon} />
            <input
              className={classNames(styles.searchInput)}
              type="text"
              id="search-input-map"
              value={bookmarksSearchValue.mapSearchValue}
              onChange={(e) => handleSearchChange('mapSearchValue', e.target.value)}
              onFocus={() => handleSearchFocus(SearchOptions.MAP)}
              placeholder='Search Map'
            />
          </div>
        </div>
      )}

      {/* Modal */}
      {isFormOpen && (
        <Modal>
          <div className={styles.AddBookmarkModal}>
            <span className={styles.AddBookmarkTitle}>Add New Bookmark</span>

            <div className={styles.AddBookmarkInputs}>
              <div className={styles.ToggleContainer}>
                <span>Set for:</span>

                <div className={styles.ToggleInputsContainer}>
                  <div className={styles.ToggleInputContainer}>
                    <input
                      className={styles.ToggleInput}
                      type='radio'
                      id='current'
                      name='current'
                      checked={!isCustomUrl}
                      onClick={() => setIsCustomUrl(!isCustomUrl)}
                    />

                    <label htmlFor='current' style={!isCustomUrl ? { fontWeight: 600 } : {}}>Current Page</label>
                  </div>

                  <div className={styles.ToggleInputContainer}>
                    <input
                      className={styles.ToggleInput}
                      type='radio'
                      id='custom'
                      name='custom'
                      checked={isCustomUrl}
                      onClick={() => setIsCustomUrl(!isCustomUrl)}
                    />

                    <label htmlFor='custom' style={isCustomUrl ? { fontWeight: 600 } : {}}>Custom Link</label>
                  </div>
                </div>
              </div>

              <div className={styles.AddBookmarkInputContainer}>
                <label htmlFor="bookmarkName">Name</label>

                <input
                  type="text"
                  id="bookmarkName"
                  value={bookmarkName}
                  onChange={(e) => setBookmarkName(e.target.value)}
                  placeholder="Enter bookmark name"
                />
              </div>

              {isCustomUrl &&
               (<div className={styles.AddBookmarkInputContainer}>

                  <label htmlFor="bookmarkURL">URL</label>

                  <input
                    type="text"
                    id="bookmarkURL"
                    value={currentUrl}
                    onChange={(e) => setCurrentUrl(e.target.value)}
                    placeholder="Enter URL"
                  />
                </div>)
              }

              <div className={styles.AddBookmarkIconsContainer}>
                <label>Select Icon</label>

                <div className={styles.AddBookmarkIcons}>
                  {BOOKMARK_ICONS.map((icon) => (
                    <img
                      key={icon}
                      className={styles.AddBookmarkIcon}
                      style={pickedIcon === icon ? { border: '1px solid #ff7c02' } : {}}
                      src={require(`../../assets/svg/bookmark/${icon}`)}
                      alt={icon}
                      onClick={() => setIcon(icon)}
                    />
                  ))}
                </div>
              </div>
            </div>

            {errors.length > 0 && (
              <div className={styles.AddBookmarkErrors}>
                {errors.map((error, index) => (
                  <span key={index} className={styles.AddBookmarkError}>
                    {error}
                  </span>
                ))}
              </div>
            )}

            <div className={styles.AddBookmarkBtns}>
              <hr className={styles.HorizontalLine} />

              <button className={styles.CancelBtn} type='button' onClick={handleModalClose}>Cancel</button>

              <button onClick={handleAddBookmark} className={styles.AddBtn} type="submit">Add</button>
            </div>
          </div>
        </Modal>
      )}

      {isCreateInventoryDeviceModalVisible && (
        <AddInventoryDeviceModal
          onClose={toggleInventoryDeviceModalVisibilyHandler}
          handleSubmit={handleSubmit}
          onSubmit={onCreateNewInventoryDeviceSubmit}
          register={register}
          errors={inventoryFormErrors}
          isSubmitting={isSubmitting}
        />
      )}

      <NoteSearchModal
        isOpen={isNotesSearchOpen}
        onClose={() => {
          setIsNotesSearchOpen(false);
          setDefaultNotesSearchToAllFromSidebar(false);
        }}
        currentNotes={isDevicePage ? currentDeviceNotes : ({} as iList<iNote>)}
        deviceOrFence="device"
        deviceOrFenceId={isDevicePage ? currentDeviceId : ''}
        ownerTitle="All Vehicles"
        noteFieldDefs={noteFieldDefs}
        globalVisibleFields={globalVisibleFields}
        defaultToAllVehiclesOnOpen={defaultNotesSearchToAllFromSidebar}
      />
  </div>
  );
};

export default BookmarksBar;
